/**
 * chaos-ui entry — bootstraps React 19 + TanStack Router and the PWA SW.
 *
 * The router tree is generated by TanStack Router Vite plugin into
 * src/routeTree.gen.ts (gitignored). Re-run `pnpm dev` once after any
 * route file is added/renamed to regenerate it.
 *
 * Service worker registration (Phase 11 / M9):
 *   - Runs only in production builds (avoids dev HMR conflicts).
 *   - Registers `/sw.js` at scope `/` so it controls the whole SPA.
 *   - Errors are swallowed (logged only) — the app must still work when
 *     SW registration fails (incognito, file://, browsers without push).
 */
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { env } from './env';
import { detectLegacyRegexBrowser } from './lib/legacy-browser';
import { readKanbanAb } from './lib/kanban/use-kanban-design';
import { routeTree } from './routeTree.gen';
import './styles/global.css';
// Design V2 "Liquid Graphite" (flag VITE_DESIGN_V2). Every rule inside is
// scoped under :root[data-design='v2'], so the sheet is fully inert — and
// neither self-hosted font downloads — until the attribute below is set.
// Imported AFTER global.css so V2 overrides win the cascade when active.
import './styles/design-v2.css';
// Design Lightening layer (flag VITE_DESIGN_LIGHT). Every rule inside every
// styles/light/* file is scoped under :root[data-light] (Kanban/card rules
// additionally under [data-kanban-ab='new']), so the whole layer is inert until
// the attributes below are set. Imported AFTER design-v2.css so the light
// overrides win the cascade when active.
import './styles/light/index.css';

// Probe for ES2018+ regex support BEFORE any component renders. Older
// engines (Safari 12 / iOS 12) crash when our markdown stack compiles
// lookbehind regexes (highlight.js, micromark internals), so we need
// the flag in place before the first lazy render can pull that chunk.
// Result lives on globalThis and is read via isLegacyRegexBrowser().
detectLegacyRegexBrowser();

// Design V2 "Liquid Graphite" — flip the token-layer switch BEFORE the first
// render so there is no V1→V2 flash. Flag OFF (default) = the attribute is
// never set = design-v2.css stays inert = the app renders byte-identical to
// today. Rollback is a rebuild with VITE_DESIGN_V2 unset/false.
if (env.VITE_DESIGN_V2) {
  document.documentElement.dataset.design = 'v2';
}

// Design Lightening layer — flip the light token/attribute switch BEFORE the
// first render so there is no flash. Flag OFF (default) = neither attribute is
// set = every styles/light/* rule stays inert = the app renders byte-identical
// to today. When ON: data-light gates the whole CSS layer; data-kanban-ab
// selects the Kanban+Cards runtime skin ('new' default, 'old' = exact current
// v2) — flippable at runtime without a rebuild via setKanbanAb().
if (env.VITE_DESIGN_LIGHT) {
  document.documentElement.dataset.light = '';
  document.documentElement.dataset.kanbanAb = readKanbanAb();
}

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Missing #root element in index.html');
}

createRoot(rootElement).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);

// ── Service worker registration ───────────────────────────────────────
// We register in both dev and prod so the install-prompt and push flows
// can be exercised end-to-end. Dev still gets a real SW because Vite
// serves the static `public/sw.js` verbatim. If you ever switch to a
// build-step-generated SW (e.g. workbox), gate this on `import.meta.env.PROD`.
//
// Auto-update contract (so a deploy never leaves a client stuck on stale
// code/data): the SW itself uses skipWaiting + clientsClaim, and here we
// (a) poll registration.update() on load, on tab refocus, and on an
// interval, so even a tab left open for days notices a new build, and
// (b) reload exactly once on `controllerchange` so the new SW's responses
// take effect immediately. Combined with API = network-first in sw.js,
// this guarantees a returning user sees fresh server data without ever
// having to manually unregister the worker or clear site data.
const SW_UPDATE_INTERVAL_MS = 60_000;

function registerServiceWorker(): void {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  // Reload once when a newly-activated SW takes control of this page, so
  // its (fresh) network-first responses replace whatever the old worker
  // was serving. We only do this when a controller already existed at
  // startup (i.e. we are swapping an OLD worker for a NEW one) — a first
  // visit has no prior controller and must not trigger a reload. The
  // one-shot flag guards against a reload loop.
  const hadControllerAtStart = Boolean(navigator.serviceWorker.controller);
  let reloadingForNewSw = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadControllerAtStart || reloadingForNewSw) return;
    reloadingForNewSw = true;
    window.location.reload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        const poll = () => {
          // Only check when the tab is visible to avoid waking the network
          // on backgrounded tabs.
          if (document.visibilityState === 'visible') {
            registration.update().catch(() => {
              /* transient network error — next tick retries */
            });
          }
        };
        // Check now, on every refocus, and on a slow interval.
        poll();
        document.addEventListener('visibilitychange', poll);
        window.setInterval(poll, SW_UPDATE_INTERVAL_MS);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[chaos-ui] SW registration failed:', err);
      });
  });
}
registerServiceWorker();
